import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  createFocusHandle,
  createOnBlur,
  FilterInputProps,
} from '../../common/tanstacktable';
import {
  DATE_FORMAT,
  FormattedDate,
  getDayjs,
  getIsoDateFromFormattedDate,
} from '../../common/utils';
import {
  FocusRef,
  PeriodEndPicker,
  PeriodEndRangePicker,
} from '../../common/components';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { Flex, Radio, RadioChangeEvent } from 'antd';
import translations from './CommentValueDateSelectFilterInput.translations.json';
import { Frequency } from '../../dataobject';

const { addTranslations, t } = i18nNsFactory(
  'comment-value-date-select-filter-input',
);
addTranslations(translations);

export interface CommentValueDateSelectFilterValueType {
  valueDates?: [FormattedDate | null, FormattedDate | null];
  frequency?: Frequency;
  type: 'single' | 'compare' | 'range';
}

export const CommentValueDateSelectFilterInput = forwardRef<
  FocusRef,
  FilterInputProps
>(({ header, onBlur }, ref) => {
  const id = `${header.column.id}-filter-input`;
  const popupId = `${id}-popup`;
  const typeId = `${id}-type`;

  useImperativeHandle(ref, () => createFocusHandle(typeId));

  const handleOnBlur = useMemo(
    () =>
      createOnBlur([id, popupId, typeId], () => {
        onBlur?.();
      }),
    [id, onBlur, popupId, typeId],
  );

  const frequency = header.column.columnDef.meta?.frequency || 'DAY';
  const filterValue = header.column.getFilterValue() as
    | CommentValueDateSelectFilterValueType
    | undefined;

  const { valueDates, type } = useMemo(
    () => ({
      valueDates: filterValue?.valueDates?.map(
        (v) => v && getDayjs(getIsoDateFromFormattedDate(v))!,
      ) as [Dayjs | null, Dayjs | null],
      type: filterValue?.type ?? 'single',
    }),
    [filterValue],
  );

  const handleTypeChange = useCallback(
    (e: RadioChangeEvent) => {
      const newType = e.target
        .value as CommentValueDateSelectFilterValueType['type'];
      header.column.setFilterValue(
        (oldValue: CommentValueDateSelectFilterValueType) => ({
          valueDates: oldValue?.valueDates,
          frequency: oldValue?.frequency,
          type: newType,
        }),
      );
    },
    [header.column],
  );

  const handleDatePickerChange = useCallback(
    (valueDate: Dayjs | [Dayjs | null, Dayjs | null] | null, index?: 0 | 1) => {
      header.column.setFilterValue(
        (old: CommentValueDateSelectFilterValueType) => {
          let valueDates = old?.valueDates ?? [null, null];

          if (Array.isArray(valueDate)) {
            // DatePicker.RangePicker provides an array of dates
            const formattedDates = valueDate.map((date) =>
              date ? (date as Dayjs).format(DATE_FORMAT) : null,
            ) as [FormattedDate | null, FormattedDate | null];
            valueDates = formattedDates;
          } else {
            // Single DatePicker provides a single date
            const formattedDate = valueDate
              ? (valueDate as Dayjs).format(DATE_FORMAT)
              : null;
            if (index !== undefined) {
              valueDates[index] = formattedDate as FormattedDate | null;
            }
          }

          return {
            valueDates,
            type,
          };
        },
      );
    },
    [header.column, type],
  );

  return (
    <Flex
      onBlur={handleOnBlur}
      className={id}
      vertical
      gap="small"
      style={{ minWidth: 300 }}
    >
      <Radio.Group
        id={typeId}
        optionType="default"
        options={[
          { label: t('single'), value: 'single' },
          { label: t('compare'), value: 'compare' },
          { label: t('range'), value: 'range' },
        ]}
        onChange={handleTypeChange}
        value={type}
        onBlur={(event) => {
          // Prevent radio group blur from propagating to avoid premature onBlur handling
          event.stopPropagation();
        }}
      />
      {type === 'range' && (
        <PeriodEndRangePicker
          id={id}
          classNames={{
            popup: { root: popupId },
          }}
          allowEmpty={[true, true]}
          allowClear
          value={valueDates}
          onChange={handleDatePickerChange}
          frequency={frequency}
        />
      )}
      <Flex gap="small">
        {(type === 'single' || type === 'compare') && (
          <PeriodEndPicker
            id={`${id}-1`}
            classNames={{
              popup: { root: popupId },
            }}
            allowClear
            format={DATE_FORMAT}
            frequency={frequency}
            value={valueDates ? valueDates[0] : null}
            onChange={(value) => handleDatePickerChange(value, 0)}
          />
        )}
        {type === 'compare' && (
          <PeriodEndPicker
            id={`${id}-2`}
            classNames={{
              popup: { root: popupId },
            }}
            allowClear
            format={DATE_FORMAT}
            frequency={frequency}
            value={valueDates ? valueDates[1] : null}
            onChange={(value) => handleDatePickerChange(value, 1)}
          />
        )}
      </Flex>
    </Flex>
  );
});
